import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ApexCharts from 'apexcharts';

declare global {
  interface Window {
    __apexcharts?: Record<string, ApexCharts>;
    ApexCharts?: typeof ApexCharts;
    __reregisterAllCharts?: () => void;
  }
}

function findChartInstanceByCanvasId(canvasId: string): ApexCharts | null {
  if (window.__apexcharts?.[canvasId]) {
    return window.__apexcharts[canvasId];
  }

  const canvas = document.querySelector(`#${canvasId}`);
  if (!canvas) return null;

  let parent = canvas.parentElement;
  let depth = 0;
  while (parent && depth < 10) {
    const reactKey = Object.keys(parent).find(
      (key) => key.startsWith('__reactInternalInstance') || key.startsWith('__reactFiber')
    );
    if (reactKey) {
      type ReactFiber = {
        memoizedProps?: { chart?: ApexCharts };
        stateNode?: { chart?: ApexCharts };
        return?: ReactFiber;
        _debugOwner?: ReactFiber;
      };
      const parentWithFiber = parent as unknown as Record<string, ReactFiber>;
      let fiber: ReactFiber | undefined = parentWithFiber[reactKey];
      while (fiber) {
        if (fiber.memoizedProps?.chart || fiber.stateNode?.chart) {
          const instance = fiber.memoizedProps?.chart || fiber.stateNode?.chart;
          if (instance && typeof instance.dataURI === 'function') {
            window.__apexcharts = window.__apexcharts || {};
            window.__apexcharts[canvasId] = instance;
            return instance;
          }
        }
        fiber = fiber.return || fiber._debugOwner;
      }
    }
    parent = parent.parentElement;
    depth++;
  }

  return null;
}

async function waitForAnyExportImages(root: HTMLElement, timeoutMs = 6000) {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const imgs = root.querySelectorAll('img[data-chart-export="true"]');
    if (imgs.length > 0) return;
    await new Promise((r) => setTimeout(r, 80));
  }

  console.warn('Timed out waiting for export images (no img[data-chart-export] found).');
}

function waitForImage(img: HTMLImageElement) {
  return new Promise<void>((resolve, reject) => {
    if (img.complete && img.naturalWidth > 0) return resolve();
    img.onload = () => resolve();
    img.onerror = (e) => reject(e);
  });
}

async function chartToPngDataUrl(chart: any, isIOS: boolean) {
  chart.update('none');
  await new Promise((r) => setTimeout(r, isIOS ? 400 : 120));
  const url = chart.toBase64Image('image/png', 1);
  return url && url !== 'data:,' ? url : null;
}

function expandScrollContainers(element: HTMLElement) {
  const targets = Array.from(
    element.querySelectorAll<HTMLElement>('[style*="overflow"], .table-container, .chart-scroller')
  );

  const originals = targets.map((el) => ({
    el,
    overflow: el.style.overflow,
    overflowX: el.style.overflowX,
    overflowY: el.style.overflowY,
    width: el.style.width,
    maxWidth: el.style.maxWidth,
  }));

  for (const el of targets) {
    el.style.overflow = 'visible';
    el.style.overflowX = 'visible';
    el.style.overflowY = 'visible';

    const fullW = el.scrollWidth;
    if (fullW && fullW > el.clientWidth) {
      el.style.width = `${fullW}px`;
      el.style.maxWidth = 'none';
    }
  }

  return () => {
    originals.forEach((o) => {
      o.el.style.overflow = o.overflow;
      o.el.style.overflowX = o.overflowX;
      o.el.style.overflowY = o.overflowY;
      o.el.style.width = o.width;
      o.el.style.maxWidth = o.maxWidth;
    });
  };
}

export async function exportElementToMultiPagePDF(
  elementOrId: HTMLElement | string,
  options?: {
    marginPx?: number;
    backgroundColor?: string;
    fileName?: string;
    forceLandscape?: boolean;
    jpegQuality?: number;
    addExportAttr?: boolean;
  }
): Promise<void> {
  const marginPx = options?.marginPx ?? 20;
  const backgroundColor = options?.backgroundColor ?? '#f5f7ff';
  const fileName = options?.fileName ?? 'export.pdf';
  const jpegQuality = options?.jpegQuality ?? 0.95;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const element =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!element) {
    console.error('Element not found:', elementOrId);
    return;
  }

  const shouldSetExportAttr = options?.addExportAttr ?? true;
  if (shouldSetExportAttr) element.setAttribute('data-export', 'true');

  const cleanupFns: Array<() => void> = [];
  try {
    if (window.__reregisterAllCharts) {
      window.__reregisterAllCharts();
    }

    await new Promise((r) => setTimeout(r, 100));

    const restoreScrolls = expandScrollContainers(element);
    cleanupFns.push(restoreScrolls);

    //Uncomment this if you revert to ChartJs Version

    // const restoreChartJs = await replaceChartJsCanvasesWithImages(element);
    // cleanupFns.push(restoreChartJs);

    await waitForAnyExportImages(element, isIOS ? 9000 : 6000);

    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const shotCanvas = await html2canvas(element, {
      backgroundColor,
      useCORS: true,
      allowTaint: true,
      scale: isIOS ? 1 : Math.min(2, window.devicePixelRatio || 1),
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: 0,
    });

    const orientation: 'p' | 'l' =
      options?.forceLandscape === true
        ? 'l'
        : options?.forceLandscape === false
          ? 'p'
          : shotCanvas.width > shotCanvas.height * 0.9
            ? 'l'
            : 'p';

    const pdf = new jsPDF(orientation, 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const pxPerMm = shotCanvas.width / pdfWidth;
    const marginMm = marginPx / pxPerMm;

    const usableWidthMm = pdfWidth - marginMm * 2;
    const usableHeightMm = pdfHeight - marginMm * 2;
    const pageHeightPx = Math.floor(usableHeightMm * pxPerMm);
    const totalPages = Math.ceil(shotCanvas.height / pageHeightPx);

    for (let page = 0; page < totalPages; page++) {
      const srcY = page * pageHeightPx;
      const remainingPx = shotCanvas.height - srcY;
      const sliceHeightPx = Math.min(pageHeightPx, remainingPx);

      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = shotCanvas.width;
      sliceCanvas.height = sliceHeightPx;

      const sliceCtx = sliceCanvas.getContext('2d')!;
      sliceCtx.fillStyle = backgroundColor;
      sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      sliceCtx.drawImage(
        shotCanvas,
        0,
        srcY,
        shotCanvas.width,
        sliceHeightPx,
        0,
        0,
        shotCanvas.width,
        sliceHeightPx
      );

      const sliceDataUrl = sliceCanvas.toDataURL('image/jpeg', jpegQuality);

      if (page > 0) pdf.addPage();

      const sliceHeightMm = sliceHeightPx / pxPerMm;
      pdf.addImage(sliceDataUrl, 'JPEG', marginMm, marginMm, usableWidthMm, sliceHeightMm);
    }

    pdf.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
    if (error instanceof Error) console.log(error.message);
  } finally {
    // restore DOM
    for (let i = cleanupFns.length - 1; i >= 0; i--) {
      try {
        cleanupFns[i]?.();
      } catch {}
    }
    if (shouldSetExportAttr) element.removeAttribute('data-export');
  }
}
